import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import PageShell from "../components/PageShell";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeftIcon,
  EditIcon,
  EyeIcon,
  SendIcon,
  SparklesIcon,
  XIcon,
} from "../components/Icons";
import "./AIWishes.css";

const AIWishes = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const festival = searchParams.get("festival");

  const { user } = useAuth();
  const senderDisplayName = useMemo(() => {
    if (!user) return "";
    return (user.name || "").trim() || user.email || "";
  }, [user]);

  const [message, setMessage] = useState("");
  const [image, setImage] = useState("");
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageChatRequest, setImageChatRequest] = useState("");
  const [messageChatRequest, setMessageChatRequest] = useState("");
  const [subject, setSubject] = useState("");
  const [companyName, setCompanyName] = useState("Your Company");
  const [directory, setDirectory] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [recipientQuery, setRecipientQuery] = useState("");
  const [recipientRoleFilter, setRecipientRoleFilter] = useState("all");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [editedMessage, setEditedMessage] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [fontStyle, setFontStyle] = useState("Arial, sans-serif");
  const [fontSize, setFontSize] = useState(14);
  const [fontColor, setFontColor] = useState(() => {
    const isDark = localStorage.getItem("darkMode") === "true";
    return isDark ? "#E5E7EB" : "#333333";
  });
  const [memoryCount, setMemoryCount] = useState(0);
  const [employeePreview, setEmployeePreview] = useState(null);
  const [employeeMessages, setEmployeeMessages] = useState({});
  const [isGeneratingMessages, setIsGeneratingMessages] = useState(false);


  const [holidayInfo, setHolidayInfo] = useState(null);
  const hasGenerated = useRef(false);


  useEffect(() => {
    const apply = () => {
      const isDark = localStorage.getItem("darkMode") === "true";
      // Only auto-switch if user hasn't explicitly picked a custom color
      if (isDark && fontColor === "#333333") setFontColor("#E5E7EB");
      if (!isDark && fontColor === "#E5E7EB") setFontColor("#333333");
    };

    apply();
    window.addEventListener("storage", apply);
    return () => window.removeEventListener("storage", apply);
  }, [fontColor]);

  // Add near the top of AIWishes component (after useState declarations)
  const filterDirectory = (list, query) => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return list || [];
    return (list || []).filter((p) => {
      const name = (p.name || "").toLowerCase();
      const email = (p.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  };

  const effectiveMessage = useMemo(
    () => (isEditing ? editedMessage : message),
    [isEditing, editedMessage, message],
  );

  useEffect(() => {
    fetchDirectory();
    if (festival) {
      if (!hasGenerated.current) {
        hasGenerated.current = true;
        generateCard();
      }
    } else {
      navigate("/select-festival");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [festival, navigate]);


const fetchDirectory = async () => {
  try {
    const response = await axios.get("/api/directory", { withCredentials: true });
    const list = Array.isArray(response.data) ? response.data : [];
    setDirectory(list);

    if (user?.role === "company_admin") {
      const defaults = list.filter(u => u?.id && u?.id !== user?.id).map(u => u.id);
      setSelectedIds(defaults);

      // Generate personalized messages once directory is loaded
      if (defaults.length > 0 && festival) {
        setIsGeneratingMessages(true);
        const employeeList = list
          .filter(e => e.id && e.id !== user?.id)
          .map(e => ({ id: e.id, name: e.name || e.email || "Employee" }));
        try {
          const msgRes = await axios.post("/api/greeting-card/generate-all-messages", {
            employees: employeeList,
            festival,
          });
          setEmployeeMessages(msgRes.data);
        } catch (e) {
          console.error("Failed to generate employee messages", e);
        } finally {
          setIsGeneratingMessages(false);
        }
      }
    }
  } catch (error) {
    console.error("Error fetching directory:", error);
  }
};


  const toggleRecipient = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const baseRecipients = useMemo(() => filterDirectory(directory), [directory]);

  const filteredRecipients = useMemo(() => {
    const q = recipientQuery.trim().toLowerCase();
    return baseRecipients.filter((u) => {
      if (!u?.id) return false;
      if (recipientRoleFilter !== "all" && u.role !== recipientRoleFilter)
        return false;
      if (!q) return true;
      const hay = `${u.name || ""} ${u.email || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [baseRecipients, recipientQuery, recipientRoleFilter]);

  const selectAll = () => {
    setSelectedIds(filteredRecipients.map((u) => u.id));
  };

  const clearAll = () => setSelectedIds([]);

  useEffect(() => {
    const fetchContext = async () => {
      if (selectedIds.length === 1 && festival) {
        try {
          const res = await axios.get(`/api/memories?space=shared&status=approved&recipient_id=${selectedIds[0]}`);
          setMemoryCount(res.data?.length || 0);

          const hRes = await axios.get(`/api/holidays?year=2026&type=all`);
          const match = hRes.data?.find(h => h.name === festival);
          setHolidayInfo(match);
        } catch (e) {
          console.error("Context fetch failed", e);
        }
      } else {
        setMemoryCount(0);
        setHolidayInfo(null);
      }
    };
    fetchContext();
  }, [selectedIds, festival]);

  const generateCard = async () => {
    setIsGenerating(true);
    let previewRecipientId = null;
    let previewEmployeeName = "Employee";
    
    if (selectedIds.length === 1) {
      const selectedEmp = directory.find(e => e.id === selectedIds[0]);
      if (selectedEmp) {
        previewRecipientId = selectedEmp.id;
        previewEmployeeName = selectedEmp.name || "Employee";
      }
    }

    try {
      const response = await axios.post("/api/greeting-card/generate", {
        festival: festival,
        employeeName: previewEmployeeName,
        companyName: companyName,
        recipient_id: previewRecipientId,
      });
      setMessage(response.data.message);
      setEditedMessage(response.data.message);
      setImage(response.data.image);
      setImagePrompt(response.data.image_prompt || "");
      setSubject(`Happy ${festival}!`);

      // Generate personalized messages for all selected employees
      if (selectedIds.length > 0) {
        setIsGeneratingMessages(true);
        const employeeList = selectedIds.map(id => {
          const emp = directory.find(e => e.id === id);
          return { id, name: emp?.name || emp?.email || "Employee" };
        });
        try {
          const msgRes = await axios.post("/api/greeting-card/generate-all-messages", {
            employees: employeeList,
            festival,
          });
          setEmployeeMessages(msgRes.data);
        } catch (e) {
          console.error("Failed to generate employee messages", e);
        } finally {
          setIsGeneratingMessages(false);
        }
      }
    } catch (error) {
      console.error("Error generating card:", error);
      const fallback = `Dear Employee,\n\nWishing you a wonderful ${festival}! We appreciate your hard work and dedication.\n\nHappy ${festival}!\n${companyName || "Your Company"}`;
      setMessage(fallback);
      setEditedMessage(fallback);

    } finally {
      setIsGenerating(false);
    }
  };


  const handleImageCustomize = async () => {
    if (!imageChatRequest.trim()) return;

    setIsGeneratingImage(true);
    try {
      const response = await axios.post("/api/greeting-card/customize-image", {
        currentImagePrompt: imagePrompt,
        userRequest: imageChatRequest,
        festival: festival,
      });

      if (response.data.image) setImage(response.data.image);
      if (response.data.image_prompt)
        setImagePrompt(response.data.image_prompt);

      setImageChatRequest("");
    } catch (error) {
      console.error("Error customizing image:", error);
      alert("Error customizing image. Please try again.");
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleMessageCustomize = async () => {
    if (!messageChatRequest.trim()) return;

    setIsGenerating(true);
    try {
      const response = await axios.post(
        "/api/greeting-card/customize-message",
        {
          currentMessage: effectiveMessage,
          userRequest: messageChatRequest,
        },
      );

      if (response.data.message) {
        setMessage(response.data.message);
        setEditedMessage(response.data.message);
      }

      setMessageChatRequest("");
    } catch (error) {
      console.error("Error customizing message:", error);
      alert("Error customizing message. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveEdit = () => {
    setMessage(editedMessage);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedMessage(message);
    setIsEditing(false);
  };

  const sendAllTargetCount = useMemo(() => {
    if (user?.role !== "company_admin") return 0;
    const selfEmail = (user?.email || "").toLowerCase();
    return directory.filter((u) => {
      if (!u?.email) return false;
      if ((u.role || "") === "company_admin") return false;
      if (u.email.toLowerCase() === selfEmail) return false;
      return true;
    }).length;
  }, [directory, user]);

  const handleSendToAll = async () => {
    if (user?.role !== "company_admin") {
      alert("Only admins can send to the entire company.");
      return;
    }

    if (sendAllTargetCount === 0) {
      alert("No recipients available for send-to-all.");
      return;
    }

    if (
      !window.confirm(`Send greeting card to ${sendAllTargetCount} recipients?`)
    ) {
      return;
    }

    setIsSending(true);
    try {
      await axios.post("/api/send-to-all", {
        festival: festival || "",
        subject: subject || `Happy ${festival}!`,
        message: effectiveMessage,
        image: image && image.length > 100 ? image : "",
        fontStyle: fontStyle,
        fontSize: fontSize,
        fontColor: fontColor,
        companyName: companyName,
      });

      alert(`Emails sent successfully to ${sendAllTargetCount} recipients!`);
    } catch (error) {
      console.error("Error sending emails:", error);
      alert("Error sending emails. Please check your email configuration.");

    } finally {
      setIsSending(false);
    }
  };


  const handleSendToSelected = async () => {
    if (!selectedIds.length) {
      alert("Please select at least one recipient.");
      return;
    }

    if (
      !window.confirm(`Send greeting to ${selectedIds.length} recipient(s)?`)
    ) {
      return;
    }

    setIsSending(true);
    try {
      await axios.post(
        "/api/greetings/send",
        {
          recipient_ids: selectedIds,
          festival: festival || "",
          subject: subject || `Happy ${festival}!`,
          message: effectiveMessage,
          personalized_messages: employeeMessages,
          image: image && image.length > 100 ? image : "",
          fontStyle,
          fontSize,
          fontColor,
          companyName,
        },
        { withCredentials: true },
      );

      alert(`Emails sent successfully to ${selectedIds.length} recipient(s)!`);
    } catch (error) {
      console.error("Error sending selected emails:", error);
      const msg = error?.response?.data?.error || "Error sending emails.";
      alert(msg);

    } finally {
      setIsSending(false);
    }
  };

  return (

    <PageShell
      title={`${festival || "Greeting"} Greeting`}
      subtitle="Generate a personalized greeting and refine it using AI."
      actions={
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => navigate("/select-festival")}
        >
          <ArrowLeftIcon className="nav-icon" />
          Back to festivals
        </button>
      }
    >
      <div className="aiGrid">
        <div className="aiLeft">
          <div className="card">
            <div className="card__header aiCardHeader">
              <div>
                <h2 className="card__title">Card image</h2>
                <p className="card__subtitle">
                  Zoom and regenerate using prompts.
                </p>
              </div>

              <div className="aiZoom">
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))}
                  disabled={zoomLevel <= 50}
                >
                  −
                </button>
                <span className="pill aiZoomPill">{zoomLevel}%</span>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setZoomLevel(Math.min(200, zoomLevel + 10))}
                  disabled={zoomLevel >= 200}
                >
                  +
                </button>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => setZoomLevel(100)}
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="card__body">
              <div className="aiImageSurface">
                {image ? (
                  <div
                    className="aiImageStage"
                    style={{
                      transform: `scale(${zoomLevel / 100})`,
                      transformOrigin: "top center",
                    }}
                  >
                    <img
                      src={`data:image/jpeg;base64,${image}`}
                      alt="Greeting card"
                    />
                  </div>
                ) : (
                  <div className="aiImageEmpty">
                    {isGenerating
                      ? "Generating image…"
                      : "Image will appear here."}
                  </div>
                )}
              </div>

              <div className="aiPromptRow">
                <div className="field aiPromptField">
                  <label className="label">Regenerate image</label>
                  <input
                    className="input"
                    placeholder="e.g., change background to winter night, add subtle snowflakes"
                    value={imageChatRequest}
                    onChange={(e) => setImageChatRequest(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleImageCustomize()
                    }
                    disabled={isGeneratingImage}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn--primary aiPromptAction"
                  onClick={handleImageCustomize}
                  disabled={isGeneratingImage || !imageChatRequest.trim()}
                >
                  <SparklesIcon className="nav-icon" />
                  {isGeneratingImage ? "Working…" : "Generate"}
                </button>
              </div>

              {imagePrompt ? (
                <div className="aiHint">
                  <span className="aiHintLabel">Current prompt:</span>
                  <span className="aiHintText">{imagePrompt}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="aiRight">
          <div className="card aiSyncPanel">
            <div className="aiSyncRow">
              <div className="aiSyncItem">
                <span className="aiSyncEmoji">📅</span>
                <span className="aiSyncText">
                  {holidayInfo 
                    ? `Calendar: ${holidayInfo.name} is on ${holidayInfo.date}`
                    : "Calendar: Standard Occasion"}
                </span>
              </div>
              <div className="aiSyncItem">
                <span className="aiSyncEmoji">🤝</span>
                <span className="aiSyncText">
                  {memoryCount > 0 
                    ? `${memoryCount} Shared Wins in Vault for personalization`
                    : "No Mutual Wins found for this employee"}
                </span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card__header">
              <h2 className="card__title">Email settings</h2>
              <p className="card__subtitle">
                Subject, sender name, and typography.
              </p>
            </div>

            <div className="card__body">
              <div className="aiFormGrid">
                <div className="field aiSpan2">
                  <label className="label">Email subject</label>
                  <input
                    className="input"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder={`Happy ${festival}!`}
                  />
                </div>

                <div className="field aiSpan2">
                  <label className="label">Sender name</label>
                  <input
                    className="input"
                    value={senderDisplayName}
                    readOnly
                    placeholder="Logged-in user"
                  />
                  <div className="aiHelp">
                    This name is automatically included in the email subject and signature.
                  </div>
                </div>

                <div className="field aiSpan2">
                  <label className="label">Company name</label>
                  <input
                    className="input"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Your Company"
                  />
                </div>

                <div className="field">
                  <label className="label">Font style</label>
                  <select
                    className="select"
                    value={fontStyle}
                    onChange={(e) => setFontStyle(e.target.value)}
                  >
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="'Times New Roman', serif">
                      Times New Roman
                    </option>
                    <option value="'Courier New', monospace">
                      Courier New
                    </option>
                    <option value="Verdana, sans-serif">Verdana</option>
                    <option value="'Trebuchet MS', sans-serif">
                      Trebuchet MS
                    </option>
                    <option value="'Comic Sans MS', cursive">
                      Comic Sans MS
                    </option>
                  </select>
                </div>

                <div className="field">
                  <label className="label">Font size</label>
                  <div className="aiInline">
                    <input
                      className="input"
                      type="number"
                      min="10"
                      max="24"
                      value={fontSize}
                      onChange={(e) =>
                        setFontSize(parseInt(e.target.value || "14", 10))
                      }
                    />
                    <span className="aiUnit">px</span>
                  </div>
                </div>

                <div className="field aiSpan2">
                  <label className="label">Font color</label>
                  <div className="aiColorRow">
                    <input
                      className="input aiColorInput"
                      type="color"
                      value={fontColor}
                      onChange={(e) => setFontColor(e.target.value)}
                    />
                    <input
                      className="input"
                      value={fontColor}
                      onChange={(e) => setFontColor(e.target.value)}
                      placeholder="#333333"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card__header aiCardHeader">
              <div>
                <h2 className="card__title">Message</h2>
                <p className="card__subtitle">
                  Edit manually or regenerate with AI.
                </p>
              </div>

              {!isEditing ? (
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => {
                    setIsEditing(true);
                    setEditedMessage(message);
                  }}
                >
                  <EditIcon className="nav-icon" />
                  Edit
                </button>
              ) : null}
            </div>

            <div className="card__body">
              <div
                className="aiMessageSurface"
                style={{ fontFamily: fontStyle, color: fontColor }}
              >
                {isEditing ? (
                  <textarea
                    className="textarea aiMessageEdit"
                    value={editedMessage}
                    onChange={(e) => setEditedMessage(e.target.value)}
                    rows={10}
                    style={{
                      fontFamily: fontStyle,
                      fontSize: `${fontSize}px`,
                      color: fontColor,
                    }}
                  />
                ) : (
                  <pre
                    style={{
                      fontFamily: fontStyle,
                      fontSize: `${fontSize}px`,
                      color: fontColor,
                    }}
                  >
                    {message || (isGenerating ? "Generating message…" : "")}
                  </pre>
                )}
              </div>

              {isEditing ? (
                <div className="aiEditActions">
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={handleSaveEdit}
                  >
                    Save
                  </button>
                </div>
              ) : null}

              <div className="aiPromptRow">
                <div className="field aiPromptField">
                  <label className="label">Regenerate message</label>
                  <input
                    className="input"
                    placeholder="e.g., make it more formal, shorter, add a light joke"
                    value={messageChatRequest}
                    onChange={(e) => setMessageChatRequest(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleMessageCustomize()
                    }
                    disabled={isGenerating || isEditing}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn--primary aiPromptAction"
                  onClick={handleMessageCustomize}
                  disabled={
                    isGenerating || isEditing || !messageChatRequest.trim()
                  }
                >
                  <SparklesIcon className="nav-icon" />
                  {isGenerating ? "Working…" : "Generate"}
                </button>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 16 }}>
            <div className="card__header">
              <div>
                <h2 className="card__title">Recipients</h2>
                <p className="card__subtitle">
                  Select who should receive this greeting.
                </p>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={selectAll}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={clearAll}
                >
                  Clear
                </button>
              </div>
            </div>

            <div
              className="card__body"
              style={{ maxHeight: 320, overflow: "auto" }}
            >
              {baseRecipients.length === 0 ? (
                <div className="pill aiWarn">
                  No available recipients for your role.
                </div>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  {/* Search / filter */}
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <input
                      className="input"
                      placeholder="Search by name or email"
                      value={recipientQuery}
                      onChange={(e) => setRecipientQuery(e.target.value)}
                      style={{ flex: "1 1 260px" }}
                    />

                    <select
                      className="input"
                      value={recipientRoleFilter}
                      onChange={(e) => setRecipientRoleFilter(e.target.value)}
                      style={{ flex: "0 0 190px" }}
                    >
                      <option value="all">All roles</option>
                      <option value="user">Employees</option>
                      <option value="team_manager">Managers</option>
                      <option value="company_admin">Admins</option>
                    </select>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ opacity: 0.85, fontSize: 13 }}>
                      Showing <strong>{filteredRecipients.length}</strong> of{" "}
                      <strong>{baseRecipients.length}</strong>.
                    </div>
                    <div style={{ opacity: 0.85, fontSize: 13 }}>
                      Selected: <strong>{selectedIds.length}</strong>
                    </div>
                  </div>

                  {/* Selected chips (lightweight preview) */}
                  {selectedIds.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {filteredRecipients
                        .filter((u) => selectedIds.includes(u.id))
                        .slice(0, 8)
                        .map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            className="pill"
                            onClick={() => toggleRecipient(u.id)}
                            title="Click to remove"
                            style={{ cursor: "pointer" }}
                          >
                            {(u.name || u.email || "").slice(0, 18)}
                          </button>
                        ))}
                      {selectedIds.length > 8 ? (
                        <span
                          style={{
                            fontSize: 12,
                            opacity: 0.75,
                            alignSelf: "center",
                          }}
                        >
                          +{selectedIds.length - 8} more
                        </span>
                      ) : null}
                    </div>
                  ) : null}

                  {/* Recipient list */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {filteredRecipients.length === 0 ? (
                      <div className="pill aiWarn">
                        No matches. Adjust search or filters.
                      </div>
                    ) : (
                      filteredRecipients.map((u) => (
                        <label
                          key={u.id}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                            padding: "10px 10px",
                            border: "1px solid var(--neutral-200)",
                            borderRadius: 12,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(u.id)}
                            onChange={() => toggleRecipient(u.id)}
                            style={{ marginTop: 3 }}
                          />
                          <div style={{ lineHeight: 1.2, width: "100%" }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 10,
                              }}
                            >
                              <div style={{ fontWeight: 650 }}>
                                {u.name || u.email}
                              </div>
                              <span className="pill" style={{ fontSize: 12 }}>
                                {u.role === "team_manager"
                                  ? "Manager"
                                  : u.role === "company_admin"
                                    ? "Admin"
                                    : "Employee"}
                              </span>
                            </div>
                            <div
                              style={{
                                opacity: 0.82,
                                fontSize: 12,
                                marginTop: 4,
                              }}
                            >
                              {u.email}
                            </div>
                            {selectedIds.includes(u.id) && (
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                style={{ marginTop: 6, fontSize: 12 }}
                                disabled={isGeneratingMessages || !employeeMessages[u.id]}
                                onClick={(e) => {
                                  e.preventDefault();
                                  const data = employeeMessages[u.id];
                                  if (data) setEmployeePreview({ name: data.name, message: data.message, loading: false });
                                }}
                              >
                                {isGeneratingMessages ? "Generating…" : employeeMessages[u.id] ? "Preview message" : "Generate card first"}
                              </button>
                            )}
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="aiActions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setShowPreview(true)}
              disabled={isSending || isGenerating}
            >
              <EyeIcon className="nav-icon" />
              Preview
            </button>

            <button
              type="button"
              className="btn btn--primary btn--block"
              onClick={handleSendToSelected}
              disabled={
                isSending ||
                isGenerating ||
                isGeneratingImage ||
                selectedIds.length === 0
              }
            >
              <SendIcon className="nav-icon" />
              {isSending
                ? "Sending…"
                : `Send to selected (${selectedIds.length})`}
            </button>

            {user?.role === "company_admin" ? (
              <button
                type="button"
                className="btn btn--secondary btn--block"
                onClick={handleSendToAll}
                disabled={
                  isSending ||
                  isGenerating ||
                  isGeneratingImage ||
                  sendAllTargetCount === 0
                }
              >
                <SendIcon className="nav-icon" />
                {isSending ? "Sending…" : `Send to all (${sendAllTargetCount})`}
              </button>
            ) : null}
          </div>

          {directory.length === 0 ? (
            <div className="pill aiWarn">
              Add people in the directory before sending.
            </div>
          ) : null}
        </div>
      </div>

      {showPreview && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={() => setShowPreview(false)}
        >
          <div
            className="modal modal--lg"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="modal__header">
              <h2 className="modal__title">Email preview</h2>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setShowPreview(false)}
              >
                <XIcon className="nav-icon" />
              </button>
            </div>

            <div className="modal__body">
              <div className="aiPreview">
                <div className="aiPreviewMeta">
                  <div className="aiPreviewRow">
                    <span className="aiPreviewLabel">Subject</span>
                    <span className="aiPreviewValue">
                      {subject || `Happy ${festival}!`}
                    </span>
                  </div>
                  <div className="aiPreviewRow">
                    <span className="aiPreviewLabel">Company</span>
                    <span className="aiPreviewValue">
                      {companyName || "Your Company"}
                    </span>
                  </div>
                </div>

                {image ? (
                  <div className="aiPreviewImage">
                    <img
                      src={`data:image/jpeg;base64,${image}`}
                      alt="Greeting card"
                    />
                  </div>
                ) : null}

                <div
                  className="aiPreviewMessage"
                  style={{
                    fontFamily: fontStyle,
                    fontSize: `${fontSize}px`,
                    color: fontColor,
                  }}
                >
                  <pre
                    style={{
                      fontFamily: fontStyle,
                      fontSize: `${fontSize}px`,
                      color: fontColor,
                    }}
                  >
                    {effectiveMessage}
                  </pre>
                </div>
              </div>
            </div>

            <div className="modal__footer">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setShowPreview(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => {
                  setShowPreview(false);
                  handleSendToAll();
                }}
                disabled={
                  isSending ||
                  isGenerating ||
                  isGeneratingImage ||
                  selectedIds.length === 0
                }
              >
                <SendIcon className="nav-icon" />
                Send
              </button>
            </div>
          </div>
        </div>
      )}
      {employeePreview && (
        <div className="modal-overlay" role="dialog" aria-modal="true"
          onMouseDown={() => setEmployeePreview(null)}>
          <div className="modal modal--lg" onMouseDown={e => e.stopPropagation()}>
            <div className="modal__header">
              <h2 className="modal__title">Preview — {employeePreview.name}</h2>
              <button type="button" className="btn btn--ghost btn--sm"
                onClick={() => setEmployeePreview(null)}>×</button>
            </div>
            <div className="modal__body" style={{ padding: 24 }}>
              {employeePreview.loading
                ? <p>Generating personalized message…</p>
                : <pre style={{ whiteSpace: "pre-wrap", fontFamily: fontStyle, fontSize: `${fontSize}px`, color: fontColor }}>
                    {employeePreview.message}
                  </pre>
              }
            </div>
            <div className="modal__footer">
              <button type="button" className="btn btn--secondary"
                onClick={() => setEmployeePreview(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

    </PageShell>

  );
};

export default AIWishes;


